// Round 1, lane B — identity alignment (docs/briefs/ROUND1-SPEC-2026-09-05.md section A.3 and
// A.5). A step's identity is its engine id, carried by the sidecar; a block's claim to that identity
// is its content signature; position is the tiebreak of last resort and only among candidates of the
// same kind whose counts agree. Answers are bound by their own ids through the same rule, never by
// index. This module is pure: it pairs indices and reports moves; the compiler reads the pairs and
// touches the clone. The frozen two-pass LCS (compile.mjs:316-343, an exact pass and then a pass on
// kind alone) is what section A.3 replaces: the kind-only pass is what cross-wired a deleted line
// with its reworded neighbour (F-01).
// The name of the coefficient, for the spell check: cspell:ignore Sørensen
import type {
  ChoiceAnswer,
  EngineId,
  SidecarBlock3,
  SidecarKind3,
} from '../../../dorian-shared/types/episodeScript';

/** The frozen `normalise` (decompile.mjs:110-113): collapse whitespace, trim, lower-case. */
export function normaliseText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

/** A.3: the word tokens of a pairing text — `normalise(x).split(' ')` minus empty strings. */
export function tokensOf(text: unknown): string[] {
  return normaliseText(text).split(' ').filter((token) => token !== '');
}

/** A.3: the similarity a pair must reach before content alone binds it. */
export const SIMILARITY_THRESHOLD = 0.5;

/**
 * A.3: Sørensen–Dice over the word tokens of two pairing texts — 2·|multiset intersection| / (|A| + |B|);
 * both empty → 1. The frozen golden edits 01, 02, 11 score 0.95, 0.77, 0.86 (pinned in
 * round1-identity.test.ts); the gate's "delete then reword" example scores below 0.5 across the
 * deleted line, which is what keeps it from cross-wiring.
 */
export function similarity(left: string, right: string): number {
  const a = tokensOf(left);
  const b = tokensOf(right);
  if (a.length === 0 && b.length === 0) return 1;
  const counts = new Map<string, number>();
  a.forEach((token) => {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  });
  let shared = 0;
  b.forEach((token) => {
    const left_ = counts.get(token) ?? 0;
    if (left_ > 0) {
      shared += 1;
      counts.set(token, left_ - 1);
    }
  });
  return (2 * shared) / (a.length + b.length);
}

/**
 * BAR-R1-01: the width of the similarity pass's tie. Two candidates this close to the best score
 * are two answers to the same question, and content alone cannot choose between them.
 */
export const SIMILARITY_TIE = 0.05;

/** How a pair was made (A.3 steps 1–4; A.5 steps 1–3). */
export type PairingRule = 'exact' | 'move' | 'similar' | 'positional';

/**
 * BAR-R1-01: why an identity could not be settled — two or more before items carry the after item's
 * normalised signature, or the similarity pass could not separate its two best candidates.
 */
export type AmbiguityReason = 'duplicate signature' | 'tied similarity' | 'contested deletion';

/** BAR-R1-01: one after item whose identity two or more before items could claim, and their indices. */
export interface AmbiguousStepMatch {
  blockIndex: number;
  entryIndices: number[];
  reason: AmbiguityReason;
}

/** BAR-R1-01: the answer-side shape of the same refusal. */
export interface AmbiguousAnswerMatch {
  parsedIndex: number;
  existingIndices: number[];
  reason: AmbiguityReason;
}

/**
 * The before side of a step alignment: a map entry with its kind, its signature and its pairing text.
 * BAR-R1-01: `raw` is the signature refined by the exact text it normalised — two items with the same
 * `raw` are the same words, letter for letter, and pair before any normalised match is considered.
 * An absent `raw` (a caller that only knows signatures) never matches, exactly as a null one.
 */
export interface AlignableEntry {
  entry: SidecarBlock3;
  kind: SidecarKind3;
  sig: string | null;
  raw?: string | null;
  text: string;
}

/** The after side: a parsed step block (opaque here) with its kind, its signature and its pairing text. */
export interface AlignableBlock<B = unknown> {
  block: B;
  kind: SidecarKind3;
  sig: string | null;
  raw?: string | null;
  text: string;
}

/** One pair of the step alignment; `moved` is decided by the longest increasing subsequence (A.3 "Moves"). */
export interface StepPair {
  entryIndex: number;
  blockIndex: number;
  rule: PairingRule;
  similarity: number | null;
  moved: boolean;
}

/**
 * alignSteps' answer: pairs in script (block) order, the entries nobody claimed, the blocks nobody
 * had, and (BAR-R1-01) the blocks whose identity two or more entries could claim. An ambiguous
 * block is in none of the other three lists, and neither are its candidates: nothing is paired,
 * nothing is deleted, nothing is inserted — the owner is asked.
 */
export interface StepAlignment {
  pairs: StepPair[];
  deletedEntries: number[];
  unpairedBlocks: number[];
  ambiguous: AmbiguousStepMatch[];
}

/** The frozen LCS on a key (compile.mjs:293-314); a null key never matches. */
function lcsPairs(beforeKeys: (string | null)[], afterKeys: (string | null)[]): [number, number][] {
  const same = (i: number, j: number): boolean => beforeKeys[i] != null && beforeKeys[i] === afterKeys[j];
  const table = Array.from({ length: beforeKeys.length + 1 }, () => new Uint32Array(afterKeys.length + 1));
  for (let row = beforeKeys.length - 1; row >= 0; row -= 1) {
    for (let column = afterKeys.length - 1; column >= 0; column -= 1) {
      table[row][column] = same(row, column)
        ? table[row + 1][column + 1] + 1
        : Math.max(table[row + 1][column], table[row][column + 1]);
    }
  }
  const pairs: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < beforeKeys.length && j < afterKeys.length) {
    if (same(i, j)) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) i += 1;
    else j += 1;
  }
  return pairs;
}

/** The free indices of one side, in order. */
function freeIndices(free: boolean[]): number[] {
  return free.map((isFree, index) => (isFree ? index : -1)).filter((index) => index >= 0);
}

/**
 * A.3 steps 1 and 2 on one key: the still-free items pair in order (an exact match), and then the
 * first free occurrence on each side of the same key pairs out of order (a move). A null key never
 * matches, so a key nobody can spell takes no part.
 */
function pairByKey(
  beforeKeys: (string | null)[],
  afterKeys: (string | null)[],
  beforeFree: boolean[],
  afterFree: boolean[],
  accept: (before: number, after: number, rule: PairingRule) => void,
): void {
  const left = freeIndices(beforeFree);
  const right = freeIndices(afterFree);
  lcsPairs(left.map((i) => beforeKeys[i]), right.map((j) => afterKeys[j]))
    .forEach(([i, j]) => accept(left[i], right[j], 'exact'));
  beforeKeys.forEach((key, i) => {
    if (!beforeFree[i] || key == null) return;
    const j = afterKeys.findIndex((other, index) => afterFree[index] && other === key);
    if (j >= 0) accept(i, j, 'move');
  });
}

/** A candidate of the similarity pass, ordered by similarity, then index distance, then position. */
interface Candidate {
  before: number;
  after: number;
  score: number;
}

function compareCandidates(left: Candidate, right: Candidate): number {
  if (left.score !== right.score) return right.score - left.score;
  const leftDistance = Math.abs(left.before - left.after);
  const rightDistance = Math.abs(right.before - right.after);
  if (leftDistance !== rightDistance) return leftDistance - rightDistance;
  if (left.before !== right.before) return left.before - right.before;
  return left.after - right.after;
}

/** The free items of one kind, on one side. */
function freeOfKind(kinds: string[], free: boolean[], kind: string): number {
  return kinds.filter((item, index) => item === kind && free[index]).length;
}

/**
 * The pairing steps 3 and 4 of A.3 and 2 and 3 of A.5, shared: among the still-free items of both
 * sides, pair by similarity (descending, ties by index distance) at or above the threshold, then,
 * per kind, pair positionally when the free counts agree. `kindOf` partitions both sides.
 * BAR-R1-01: before any of that binds an id, `refuse` is offered every after item whose candidates
 * content cannot separate — two scores inside the tie window, or a kind whose free before items
 * outnumber its free after items, which is the delete-and-retype shape (one of these lines was
 * deleted and the other reworded, and the words alone do not say which).
 * F14b: `allowPositional` is asked once, after the similarity pass and before the positional one,
 * and a `false` leaves every still-free item where it is. Only the answer side passes it (position
 * on a choice is not identity while an identity on that choice is unsettled); the step side does
 * not, so F13's positional pairing between narration lines is exactly what it was.
 * F14c: this function is unchanged by that rule. What an ambiguous choice may keep is decided by
 * its caller, through the two callbacks it hands in. Note the order this function runs them in: the
 * refusal loop below completes before the first `accept`, so within one call every refusal precedes
 * every binding. That is what makes the caller's guard sufficient — a `refuse` here is seen before
 * anything is offered to `accept`, and before `allowPositional` is asked.
 */
function pairBySimilarityThenPosition(
  beforeTexts: string[],
  afterTexts: string[],
  beforeKinds: string[],
  afterKinds: string[],
  beforeFree: boolean[],
  afterFree: boolean[],
  accept: (before: number, after: number, rule: PairingRule, score: number | null) => void,
  refuse: (before: number[], after: number, reason: AmbiguityReason) => void,
  allowPositional: () => boolean = () => true,
): void {
  const candidates: Candidate[] = [];
  for (let i = 0; i < beforeTexts.length; i += 1) {
    if (beforeFree[i]) {
      for (let j = 0; j < afterTexts.length; j += 1) {
        if (afterFree[j] && beforeKinds[i] === afterKinds[j]) {
          const score = similarity(beforeTexts[i], afterTexts[j]);
          if (score >= SIMILARITY_THRESHOLD) candidates.push({ before: i, after: j, score });
        }
      }
    }
  }
  const byAfter = new Map<number, Candidate[]>();
  candidates.forEach((candidate) => {
    const known = byAfter.get(candidate.after);
    if (known) known.push(candidate);
    else byAfter.set(candidate.after, [candidate]);
  });
  [...byAfter.keys()].sort((left, right) => left - right).forEach((after) => {
    if (!afterFree[after]) return;
    const ranked = (byAfter.get(after) ?? []).filter((candidate) => beforeFree[candidate.before]).sort(compareCandidates);
    if (ranked.length < 2) return;
    const tied = ranked[1].score >= ranked[0].score - SIMILARITY_TIE;
    const contested = freeOfKind(beforeKinds, beforeFree, afterKinds[after])
      > freeOfKind(afterKinds, afterFree, afterKinds[after]);
    if (!tied && !contested) return;
    const claimants = ranked.map((candidate) => candidate.before).sort((left, right) => left - right);
    refuse(claimants, after, tied ? 'tied similarity' : 'contested deletion');
  });
  candidates.sort(compareCandidates).forEach((candidate) => {
    if (!beforeFree[candidate.before] || !afterFree[candidate.after]) return;
    accept(candidate.before, candidate.after, 'similar', candidate.score);
  });
  if (!allowPositional()) return;
  const kinds = new Set([...beforeKinds, ...afterKinds]);
  kinds.forEach((kind) => {
    const leftovers = beforeKinds.map((item, index) => (item === kind && beforeFree[index] ? index : -1)).filter((index) => index >= 0);
    const newcomers = afterKinds.map((item, index) => (item === kind && afterFree[index] ? index : -1)).filter((index) => index >= 0);
    if (leftovers.length === 0 || leftovers.length !== newcomers.length) return;
    leftovers.forEach((before, at) => {
      accept(before, newcomers[at], 'positional', null);
    });
  });
}

/** The indices (into `sequence`) of one longest strictly increasing subsequence, chosen deterministically. */
function longestIncreasingSubsequence(sequence: number[]): Set<number> {
  const tails: number[] = [];
  const tailIndices: number[] = [];
  const previous: number[] = new Array(sequence.length).fill(-1);
  sequence.forEach((value, index) => {
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const middle = (low + high) >> 1; // eslint-disable-line no-bitwise -- integer midpoint
      if (tails[middle] < value) low = middle + 1;
      else high = middle;
    }
    tails[low] = value;
    tailIndices[low] = index;
    previous[index] = low > 0 ? tailIndices[low - 1] : -1;
  });
  const kept = new Set<number>();
  let cursor = tailIndices.length ? tailIndices[tailIndices.length - 1] : -1;
  while (cursor >= 0) {
    kept.add(cursor);
    cursor = previous[cursor];
  }
  return kept;
}

/**
 * A.3 in the BAR-R1-01 order: align one scene's map entries (map order) with its parsed step blocks
 * (script order).
 * 1. exact RAW text, in order, and 2. out of order (a move): two lines that read the same letter for
 *    letter are the same line, so swapping two lines that differ only in case or spacing moves the
 *    objects and never writes one line's words over the other's id;
 * 3. an after item no raw text claimed, whose normalised signature two or more entries carry, is
 *    ambiguous: nothing is paired for it or for them, and the owner is asked;
 * 4. exact signature, in order, and 5. out of order (a move); 6. similar (Dice ≥ 0.5 on the pairing
 *    text, same kind), which refuses a tie or a contested deletion the same way; 7. equal-count
 *    positional per kind; 8. leftovers are deletions and insertions.
 * Then the longest increasing subsequence of map indices, taken in script order, stays put and every
 * other pair is a move.
 */
export function alignSteps(before: AlignableEntry[], after: AlignableBlock[]): StepAlignment {
  const beforeFree = before.map(() => true);
  const afterFree = after.map(() => true);
  const pairs: StepPair[] = [];
  const ambiguous: AmbiguousStepMatch[] = [];
  const accept = (entryIndex: number, blockIndex: number, rule: PairingRule, score: number | null): void => {
    beforeFree[entryIndex] = false;
    afterFree[blockIndex] = false;
    pairs.push({
      entryIndex, blockIndex, rule, similarity: score, moved: false,
    });
  };
  const refuse = (entryIndices: number[], blockIndex: number, reason: AmbiguityReason): void => {
    entryIndices.forEach((entryIndex) => {
      beforeFree[entryIndex] = false;
    });
    afterFree[blockIndex] = false;
    ambiguous.push({ blockIndex, entryIndices, reason });
  };
  // 1 and 2. Exact raw text, in order and then out of order.
  pairByKey(
    before.map((item) => item.raw ?? null),
    after.map((item) => item.raw ?? null),
    beforeFree,
    afterFree,
    (i, j, rule) => accept(i, j, rule, null),
  );
  // 3. What the raw text left behind and the signature alone cannot tell apart.
  after.forEach((block, j) => {
    if (!afterFree[j] || block.sig == null) return;
    const claimants = before
      .map((entry, i) => (beforeFree[i] && entry.sig === block.sig ? i : -1))
      .filter((i) => i >= 0);
    if (claimants.length >= 2) refuse(claimants, j, 'duplicate signature');
  });
  // 4 and 5. Exact signature, in order and then out of order.
  pairByKey(
    before.map((item) => item.sig),
    after.map((item) => item.sig),
    beforeFree,
    afterFree,
    (i, j, rule) => accept(i, j, rule, null),
  );
  // 6. Similar, then 7. equal-count positional per kind.
  pairBySimilarityThenPosition(
    before.map((item) => item.text),
    after.map((item) => item.text),
    before.map((item) => item.kind),
    after.map((item) => item.kind),
    beforeFree,
    afterFree,
    accept,
    refuse,
  );
  pairs.sort((left, right) => left.blockIndex - right.blockIndex);
  const kept = longestIncreasingSubsequence(pairs.map((pair) => pair.entryIndex));
  pairs.forEach((pair, index) => {
    // The object is the alignment's own; marking the move in place keeps the pairs array as one list.
    // eslint-disable-next-line no-param-reassign
    pair.moved = !kept.has(index);
  });
  return {
    pairs,
    deletedEntries: freeIndices(beforeFree),
    unpairedBlocks: freeIndices(afterFree),
    ambiguous: ambiguous.sort((left, right) => left.blockIndex - right.blockIndex),
  };
}

/**
 * The before side of an answer alignment: an existing answer, read from the clone in `answerIds`
 * order. BAR-R1-01b: `price` and `destination` are the rest of what the writer SEES on the line —
 * `* text [price] → TARGET` — so two answers whose words are the same are still told apart by the
 * line as a whole. Both are optional: a caller that knows only the words gets the frozen behaviour,
 * every answer at price 0 with no destination.
 */
export interface AnswerIdentity {
  id: EngineId;
  text: string | null | undefined;
  price?: number | null;
  destination?: EngineId | null;
}

/**
 * BAR-R1-01b: the whole visible line as one key — the words letter for letter, the price the
 * bracket spells, and the destination the arrow resolves to. Two answers this string tells apart
 * are two different lines on the page; two it does not are the same line to the writer, and no
 * pairing of them can be read off the script.
 */
function visibleLine(text: unknown, price: unknown, destination: unknown): string {
  return JSON.stringify([String(text ?? ''), Number(price ?? 0), destination == null ? null : String(destination)]);
}

/** One pair of the answer alignment. */
export interface AnswerPair {
  existingIndex: number;
  parsedIndex: number;
  rule: PairingRule;
}

/**
 * alignAnswers' answer: pairs in script (parsed) order, removed existing answers, added parsed
 * answers, and (BAR-R1-01) the parsed answers whose identity two or more existing answers could
 * claim. An ambiguous answer is in none of the other three lists, and neither are its candidates.
 */
export interface AnswerAlignment {
  pairs: AnswerPair[];
  removed: number[];
  added: number[];
  reordered: boolean;
  ambiguous: AmbiguousAnswerMatch[];
}

/**
 * A.5 in the BAR-R1-01b order. `destinations` is the branch each parsed answer's arrow resolves to,
 * in script order; without it every parsed answer is read as having no destination.
 * 1. exact on the whole VISIBLE LINE — words, price and destination together — first free on each
 *    side, so swapping two answers that differ only in their price or their destination moves the
 *    answer OBJECTS (their ids, rewards and routes travel) instead of repricing the ids that stayed
 *    put, and an untouched choice settles here whatever its answers read like;
 * 2. BAR-R1-01b: a visible line two or more existing answers spell the same way that pass 1 did NOT
 *    settle — the script no longer spells it once for each of them. Which object a remaining line
 *    names is not on the page, so the group binds no id at all: pass 1's pairings inside it are
 *    taken back, every free line of the script that spells those words is refused with it, and
 *    nothing is paired, repriced, added or removed for any of them;
 * 3. exact on the RAW text (BAR-R1-01), which is what pairs an answer whose price or destination the
 *    writer changed, and what makes a swap of two answers differing only in case a move;
 * 4. a parsed answer no earlier pass claimed whose normalised text two or more existing answers
 *    carry is ambiguous; 5. exact on `normalise(text)`; 6. similar (Dice ≥ 0.5, descending, ties by
 *    index distance), which refuses a tie or a contested deletion; 7. equal-count positional —
 *    F14c: 6 AND 7 run ONLY while this choice has raised no refusal at all, and a refusal raised
 *    inside 6 takes back whatever 6 had already bound here, so on a choice one of whose identities
 *    is unsettled the pairs that stand are the exact-text ones alone and the leftovers stay
 *    leftovers;
 * 8. leftovers: an unpaired existing answer is a removal, an unpaired parsed answer an addition;
 * 9. `reordered` when the paired answers' script order differs from the clone's order.
 */
export function alignAnswers(
  before: AnswerIdentity[],
  after: ChoiceAnswer[],
  destinations?: (EngineId | null)[],
): AnswerAlignment {
  const beforeFree = before.map(() => true);
  const afterFree = after.map(() => true);
  const pairs: AnswerPair[] = [];
  const ambiguous: AmbiguousAnswerMatch[] = [];
  const accept = (existingIndex: number, parsedIndex: number, rule: PairingRule): void => {
    beforeFree[existingIndex] = false;
    afterFree[parsedIndex] = false;
    pairs.push({ existingIndex, parsedIndex, rule });
  };
  const refuse = (existingIndices: number[], parsedIndex: number, reason: AmbiguityReason): void => {
    existingIndices.forEach((existingIndex) => {
      beforeFree[existingIndex] = false;
    });
    afterFree[parsedIndex] = false;
    ambiguous.push({ parsedIndex, existingIndices, reason });
  };
  const beforeRaw = before.map((answer) => String(answer.text ?? ''));
  const afterRaw = after.map((answer) => String(answer.text ?? ''));
  const beforeTexts = beforeRaw.map(normaliseText);
  const afterTexts = afterRaw.map(normaliseText);
  const pairByText = (texts: string[], wanted: string[]): void => {
    after.forEach((_, j) => {
      if (!afterFree[j]) return;
      const i = texts.findIndex((text, index) => beforeFree[index] && text === wanted[j]);
      if (i >= 0) accept(i, j, 'exact');
    });
  };
  const beforeVisible = before.map((answer) => visibleLine(answer.text, answer.price, answer.destination));
  const afterVisible = after.map((answer, index) => visibleLine(
    answer.text,
    answer.price,
    destinations ? destinations[index] ?? null : null,
  ));
  // 1. The whole visible line, exactly: the words, the price and the destination together. This
  //    runs FIRST, so a choice whose script still spells every one of its answers exactly as the
  //    clone holds them settles here and says nothing, however many of those lines read alike.
  pairByText(beforeVisible, afterVisible);
  // 2. BAR-R1-01b: a visible line two or more existing answers spell the same way, which pass 1 did
  //    NOT settle — the script no longer spells that line once for each of them. Nothing on the page
  //    says which of the group a remaining line is, so the whole group is refused together: any
  //    pairing pass 1 made inside it is taken back (an exact match cannot be trusted while a twin of
  //    it is unaccounted for), and every free line of the script that spells those words is refused
  //    with it. The owner is asked instead of an id being repriced, deleted or added on a guess.
  const visibleGroups = new Map<string, number[]>();
  beforeVisible.forEach((line, index) => {
    const known = visibleGroups.get(line);
    if (known) known.push(index);
    else visibleGroups.set(line, [index]);
  });
  visibleGroups.forEach((claimants, line) => {
    if (claimants.length < 2) return;
    if (claimants.every((index) => !beforeFree[index])) return;
    const inside = new Set(claimants);
    pairs.filter((pair) => inside.has(pair.existingIndex)).forEach((pair) => {
      beforeFree[pair.existingIndex] = true;
      afterFree[pair.parsedIndex] = true;
      pairs.splice(pairs.indexOf(pair), 1);
    });
    const words = new Set(claimants.map((index) => beforeTexts[index]));
    after.forEach((_, j) => {
      if (!afterFree[j]) return;
      if (afterVisible[j] === line || words.has(afterTexts[j])) refuse(claimants, j, 'duplicate signature');
    });
  });
  // 3. The exact words, letter for letter (BAR-R1-01) — a price or a destination the writer changed.
  pairByText(beforeRaw, afterRaw);
  // 4. What those left behind and the normalised text cannot tell apart.
  after.forEach((_, j) => {
    if (!afterFree[j]) return;
    const claimants = beforeTexts
      .map((text, i) => (beforeFree[i] && text === afterTexts[j] ? i : -1))
      .filter((i) => i >= 0);
    if (claimants.length >= 2) refuse(claimants, j, 'duplicate signature');
  });
  // 5. The frozen normalised match.
  pairByText(beforeTexts, afterTexts);
  const kinds = before.map(() => 'answer');
  // 6 and 7. F14c (NF's disposition of 2026-09-07, which supersedes F14b's narrower gate): once ANY
  //    identity on this choice is unsettled, the only pairs that stand on it are the exact-text ones
  //    passes 1, 3 and 5 made. Neither how a line reads nor where it sits may name an answer here.
  //    F14b gated position alone and left similarity running, on the reasoning that reading like one
  //    particular answer is evidence off the page; BAR-F14B-01 is where that reasoning breaks. The
  //    refusal consumes the twins, so the line the writer reworded is left facing a DIFFERENT answer
  //    that it half resembles, its true kin already taken out of the running — and the words land on
  //    that answer's id, with its price and its route stripped on the way. The similarity score is
  //    only evidence while every candidate is still in the running, and on a refused choice they are
  //    not. So both arms are skipped once anything here is ambiguous, and a refusal raised INSIDE
  //    this pass (a tie, or a contested deletion) stops it too. The mechanism is the guard: once
  //    `refusedHere` is set, `accept` returns without binding and `allowPositional` answers false.
  //    That is enough because the pass raises every refusal it will raise before it binds anything,
  //    so no pair can predate one. The `pairs.splice(boundHere)` take-back below is belt and braces
  //    for the opposite order, which is proved unreachable today (a probe over 191,072 shapes took
  //    back nothing); it would become live if a refusal could ever follow a binding here. The
  //    leftovers stay leftovers — free lines become additions and unclaimed answers become removals,
  //    which the compiler holds — and the writer resolves the duplicate first. A choice with nothing
  //    ambiguous on it runs both arms exactly as it did.
  if (ambiguous.length === 0) {
    const boundHere = pairs.length;
    const refusedExisting = new Set<number>();
    const refusedParsed = new Set<number>();
    let refusedHere = false;
    pairBySimilarityThenPosition(
      beforeRaw,
      after.map((answer) => answer.text),
      kinds,
      after.map(() => 'answer'),
      beforeFree,
      afterFree,
      (i, j, rule) => {
        if (refusedHere) return;
        accept(i, j, rule);
      },
      (existingIndices, parsedIndex, reason) => {
        refusedHere = true;
        existingIndices.forEach((existingIndex) => refusedExisting.add(existingIndex));
        refusedParsed.add(parsedIndex);
        refuse(existingIndices, parsedIndex, reason);
      },
      () => !refusedHere,
    );
    if (refusedHere) {
      // Whatever this pass bound before the refusal was bound on the evidence the refusal has just
      // disqualified, so it is taken back and both its sides go free. A side the refusal itself
      // claimed stays claimed: it belongs to the ambiguity now, not to the leftovers.
      pairs.splice(boundHere).forEach((pair) => {
        if (refusedExisting.has(pair.existingIndex) || refusedParsed.has(pair.parsedIndex)) return;
        beforeFree[pair.existingIndex] = true;
        afterFree[pair.parsedIndex] = true;
      });
    }
  }
  pairs.sort((left, right) => left.parsedIndex - right.parsedIndex);
  const reordered = pairs.some((pair, index) => index > 0 && pair.existingIndex < pairs[index - 1].existingIndex);
  return {
    pairs,
    removed: freeIndices(beforeFree),
    added: freeIndices(afterFree),
    reordered,
    ambiguous: ambiguous.sort((left, right) => left.parsedIndex - right.parsedIndex),
  };
}
